import { API_URL } from '../config/constants';
import { buildAuthHeaders, isTokenExpired } from '../utils/auth';

// Interface for annotation position
export interface AnnotationPosition {
  page: number;
  startOffset: number;
  endOffset: number;
}

// Interface for the base annotation
export interface BaseAnnotation {
  id: string | number;
  type: 'highlight' | 'note' | 'bookmark';
  position: AnnotationPosition;
  createdAt: Date | string;
}

// Interface for highlight annotations
export interface Highlight extends BaseAnnotation {
  type: 'highlight';
  content: string;
  color: string;
}

// Interface for note annotations
export interface Note extends BaseAnnotation {
  type: 'note';
  content: string;
}

// Interface for bookmark annotations
export interface Bookmark extends BaseAnnotation {
  type: 'bookmark';
  title?: string;
  description?: string;
}

// Union type for all annotation types
export type Annotation = Highlight | Note | Bookmark;

// Response from the API for annotations
export interface AnnotationsResponse {
  notes: (Highlight | Note)[];
  bookmarks: Bookmark[];
}

// Annotations service
const AnnotationsService = {
  // Get all annotations for a book
  async getAnnotations(bookId: number, token: string): Promise<Annotation[]> {
    try {
      if (!token || isTokenExpired(token)) {
        return [];
      }

      const response = await fetch(`${API_URL}/annotations/book/${bookId}`, {
        method: 'GET',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        return [];
      }

      const data: AnnotationsResponse = await response.json();
      
      // Combine notes and bookmarks into a single array
      return [...data.notes, ...data.bookmarks];
    } catch (error) {
      console.error('Error fetching annotations:', error);
      return [];
    }
  },

  // Create a highlight
  async createHighlight(
    bookId: number, 
    pageNumber: number, 
    content: string, 
    color: string, 
    position: { startOffset: number, endOffset: number },
    token: string
  ): Promise<Highlight | null> {
    try {
      if (!token || isTokenExpired(token)) {
        return null;
      }

      const response = await fetch(`${API_URL}/annotations/note`, {
        method: 'POST',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          bookId,
          pageNumber,
          content,
          color,
          position
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create highlight');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating highlight:', error);
      return null;
    }
  },

  // Create a note
  async createNote(
    bookId: number, 
    pageNumber: number, 
    content: string, 
    position: { startOffset: number, endOffset: number },
    token: string
  ): Promise<Note | null> {
    try {
      if (!token || isTokenExpired(token)) {
        return null;
      }

      const response = await fetch(`${API_URL}/annotations/note`, {
        method: 'POST',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          bookId,
          pageNumber,
          content,
          position
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create note');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating note:', error);
      return null;
    }
  },

  // Create a bookmark
  async createBookmark(
    bookId: number, 
    pageNumber: number, 
    title?: string, 
    description?: string,
    token?: string
  ): Promise<Bookmark | null> {
    try {
      if (!token || isTokenExpired(token)) {
        throw new Error('Authentication token is required');
      }

      const response = await fetch(`${API_URL}/annotations/bookmark`, {
        method: 'POST',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          bookId,
          pageNumber,
          title,
          description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create bookmark');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating bookmark:', error);
      return null;
    }
  },

  // Delete a note or highlight
  async deleteNote(id: string | number, token: string): Promise<boolean> {
    try {
      if (!token || isTokenExpired(token)) {
        return false;
      }

      const response = await fetch(`${API_URL}/annotations/note/${id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token)
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting note:', error);
      return false;
    }
  },

  // Delete a bookmark
  async deleteBookmark(id: string | number, token: string): Promise<boolean> {
    try {
      if (!token || isTokenExpired(token)) {
        return false;
      }

      const response = await fetch(`${API_URL}/annotations/bookmark/${id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token)
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      return false;
    }
  }
};

export default AnnotationsService; 
